import React, { useState, useCallback } from 'react';
import {
  Eye,
  Edit,
  Trash2,
  Mail,
  RefreshCw,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Calendar,
  XCircle,
  Clock,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Box,
  Typography,
  Collapse,
  useTheme
} from '@mui/material';

const Table1_2 = ({
  data,
  columns,
  onEdit,
  onView,
  onDelete,
  onSendEmail,
  onReschedule,
  defaultActions = true,
  rowExpansion,
  profileImages = {}
}) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState({});

  const handleExpandRow = useCallback((id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  }, []);

  const getStatusBadge = (status) => {
    const config = {
      SCHEDULED: { color: 'warning.main', icon: Calendar },
      COMPLETED: { color: 'success.main', icon: CheckCircle },
      CANCELLED: { color: 'error.main', icon: XCircle },
    };
    const { color, icon: Icon } = config[status] || { color: 'text.secondary', icon: Clock };
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: color }}>
        {Icon && <Icon size={14} />}
        <Typography variant="body2" component="span" fontWeight="bold">
          {status}
        </Typography>
      </Box>
    );
  };

  return (
    <TableContainer component={Paper} elevation={0} sx={{
      width: '100%',
      overflowX: 'auto',
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`
    }}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell
                key={index}
                onClick={col.onClick}
                sx={{
                  fontWeight: 'bold',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  cursor: col.onClick ? 'pointer' : 'default',
                  whiteSpace: 'nowrap'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {col.header}
                  {col.sortIcon && col.sortIcon}
                </Box>
              </TableCell>
            ))}
            {defaultActions && (
              <TableCell sx={{
                fontWeight: 'bold',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
              }}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <React.Fragment key={item.id}>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}
              >
                {columns.map((col, index) => (
                  <TableCell key={index} component="th" scope="row">
                    {col.render ? col.render(item) : item[col.key]}
                  </TableCell>
                ))}
                {defaultActions && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {rowExpansion && (
                        <IconButton
                          onClick={() => handleExpandRow(item.id)}
                          size="small"
                        >
                          {expandedRows[item.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </IconButton>
                      )}
                      {onView && (
                        <IconButton onClick={() => onView(item)} size="small" color="primary">
                          <Eye size={16} />
                        </IconButton>
                      )}
                      {onEdit && (
                        <IconButton onClick={() => onEdit(item)} size="small" color="info">
                          <Edit size={16} />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton onClick={() => onDelete(item.id)} size="small" color="error">
                          <Trash2 size={16} />
                        </IconButton>
                      )}
                      {onSendEmail && (
                        <IconButton onClick={() => onSendEmail(item)} size="small">
                          <Mail size={16} />
                        </IconButton>
                      )}
                      {onReschedule && (
                        <IconButton onClick={() => onReschedule(item)} size="small">
                          <RefreshCw size={16} />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
              {expandedRows[item.id] && rowExpansion && (
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + (defaultActions ? 1 : 0)}>
                    <Collapse in={expandedRows[item.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        {rowExpansion(item)}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Table1_2;